import React, { memo, ReactElement, useCallback, useContext, useRef, useState } from "react";

// defs
import { RouteProps } from "@payment-client/interfaces/Router";
import {
    ConfirmPennyTransactionResponse,
    CreatePennyTransactionResponse,
    PAYMENT_CODES,
} from "@swiggy-private/payment-types";

// helpers
import {
    CheckoutActionTypes,
    CheckoutContext,
    TransactionReqData,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_PENNY_TXN_CONFIRM_ORDER_STATE,
} from "@swiggy-private/checkout-core";
import { getConfirmOrderTexts } from "@payment-client/helpers/PaymentHelper";

// hooks
import usePennyTxnStatusPolling from "@payment-client/hooks/usePennyTxnStatusPolling";

// components
import PennyTxnStatusPollerWrapper from "./PennyTxnStatusPollerWrapper";

// constants
import { TICK_STATES, UpiIntentPoller } from "@swiggy-private/payment-ui";
import { TEXT_TYPES } from "@payment-client/constants/ConfirmScreen";

interface PennyTxnStatusPollerContentProps {
    transaction: CreatePennyTransactionResponse & ConfirmPennyTransactionResponse;
    orderId: string;
    transactionReqData: TransactionReqData;
    useJuspaySDK?: boolean;
}

export function PennyTxnStatusPoller(_props: RouteProps): ReactElement | null {
    const { checkoutState } = useContext(CheckoutContext);

    if (checkoutState.type !== CheckoutActionTypes.PENNY_TXN_STATUS_POLLER) {
        return null;
    }

    return (
        <PennyTxnStatusPollerContent
            transaction={checkoutState.payload.transaction}
            orderId={checkoutState.payload.orderId}
            transactionReqData={checkoutState.payload.transactionReqData}
            useJuspaySDK={checkoutState.payload.useJuspaySDK}
        />
    );
}

const PennyTxnStatusPollerContent = memo(function PennyTxnStatusPollerContent({
    transaction,
    orderId,
    transactionReqData,
    useJuspaySDK,
}: PennyTxnStatusPollerContentProps): React.ReactElement {
    const [showCancelModal, setShowCancelModal] = useState(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const mountTimeRef = useRef(Date.now());

    const tickScreenState = getConfirmOrderTexts({ tickState: TICK_STATES.LOADING, isPennyDrop: true });

    const txnStatusStop = usePennyTxnStatusPolling({
        pollingDurationInMs: transaction.maxTimeToPollForInMs || 0,
        pollingIntervalInMs: transaction.pollingIntervalInMs || 0,
        startTime: Date.now(),
        paymentCode: PAYMENT_CODES.JUSPAY,
    });

    const onBack = useCallback(() => {
        setShowCancelModal(true);
    }, []);

    const onClickCancel = useCallback(
        (show: boolean): void => {
            setShowCancelModal(show);
        },
        [setShowCancelModal],
    );

    const onCancelTransaction = useCallback(() => {
        txnStatusStop();

        // (3.6) User cancelled the penny drop — lets us measure cancellation rate.
        recordNREvent({
            eventType: NEWRELIC_TABLES.WP_PENNY_TXN_CONFIRM_ORDER,
            state: WP_PENNY_TXN_CONFIRM_ORDER_STATE.PENNY_TXN_USER_CANCELLED,
            orderId,
            transactionId: transaction.paymentTxnId,
            elapsedMs: Date.now() - mountTimeRef.current,
        });

        setCheckoutState({
            type: CheckoutActionTypes.CONFIRM_TRANSACTION,
            payload: {
                orderId,
                transaction,
                transactionReqData,
                useJuspaySDK,
            },
        });
    }, [txnStatusStop, setCheckoutState, orderId, transaction, transactionReqData, useJuspaySDK]);

    return (
        <PennyTxnStatusPollerWrapper onBack={onBack} transaction={transaction}>
            <UpiIntentPoller
                tickState={TICK_STATES.LOADING}
                mainText={tickScreenState[TEXT_TYPES.MAIN]}
                subText={tickScreenState[TEXT_TYPES.SUB]}
                showCancelModal={showCancelModal}
                setShowCancelModal={onClickCancel}
                onCancelTransaction={onCancelTransaction}
                paymentAmount={String(transaction.amount)}
            />
        </PennyTxnStatusPollerWrapper>
    );
});
